import type { Message } from "./Message";
import { type MessageFilter } from "./MessageFilter";

export interface IterLogTimeBatchesOptions {
  filter?: MessageFilter<bigint>;
  signal?: AbortSignal;
}
export interface IterMessagesOptions {
  filter?: MessageFilter<bigint>;
  messagePathsInclude: string[]; // expected to be message path ids
  signal?: AbortSignal;
}

export type DownloadPriority = "foreground" | "background";

export interface TopicReaderEvents {
  downloadstart: CustomEvent<{ id: symbol; priority: DownloadPriority }>;
  downloadend: CustomEvent<{ id: symbol }>;
}

export class TopicReaderEventTarget extends EventTarget {
  private listeners: {
    type: string;
    listener: EventListenerOrEventListenerObject;
  }[] = [];

  addEventListener<K extends keyof TopicReaderEvents>(
    type: K,
    listener: (ev: TopicReaderEvents[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void {
    super.addEventListener(type, listener, options);
    const alreadyTracked = this.listeners.some(
      (entry) => entry.type === type && entry.listener === listener,
    );
    if (!alreadyTracked) {
      this.listeners.push({ type, listener });
    }
  }

  removeEventListener<K extends keyof TopicReaderEvents>(
    type: K,
    listener: (ev: TopicReaderEvents[K]) => void,
    options?: boolean | EventListenerOptions,
  ): void;
  removeEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean,
  ): void {
    super.removeEventListener(type, callback, options);
    this.listeners = this.listeners.filter(
      (listener) => listener.type !== type || listener.listener !== callback,
    );
  }

  removeAllListeners(): void {
    this.listeners.forEach(({ type, listener }) =>
      super.removeEventListener(type, listener),
    );
    this.listeners.length = 0;
  }
}

export type LogTimeIndexEntry = [
  // logTime
  bigint,
  // sequence/row index/unfiltered iteration order (id)
  number,
];

/**
 * Encapsulates the logic for reading messages from a topic,
 * regardless of the source format of its bytes or if (and how) the bytes are chunked across many files.
 *
 * A TopicReader coordinates pulling bytes from one or many topic data representation files,
 * and acts as a read-through cache. Caching strategies are different for different data formats,
 * but all TopicReaders use the Origin Private File System as the local cache and S3 as the remote.
 */
export interface TopicReader extends TopicReaderEventTarget {
  topicId: string;
  dispose(): void;
  iterLogTimeBatches(
    options?: IterLogTimeBatchesOptions,
  ): AsyncGenerator<LogTimeIndexEntry[], void, undefined>;
  iterMessages(
    options?: IterMessagesOptions,
  ): AsyncGenerator<Message, void, undefined>;
}
